// ============================================================
// ROUTE GUARD
// Redirects unauthenticated or wrong-role users.
// ============================================================

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import type { UserRole } from '@jobnvisa/shared';
import { LoadingScreen } from './LoadingScreen';

interface RouteGuardProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireVerified?: boolean;
}

/**
 * Wraps protected routes. Redirects to:
 * - /auth/login       if not authenticated
 * - /auth/pending     if account is pending_verification
 * - /auth/suspended   if account is suspended or banned
 * - /unauthorized     if authenticated but wrong role
 */
export function RouteGuard({
  children,
  allowedRoles,
  requireVerified = true,
}: RouteGuardProps) {
  const { firebaseUser, userDoc, role, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Not authenticated
  if (!firebaseUser) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // User doc not yet synced
  if (!userDoc) {
    return <LoadingScreen message="Loading your profile..." />;
  }

  console.log('[RouteGuard] Trace:', {
    pathname: location.pathname,
    emailVerified: userDoc?.emailVerified,
    phoneVerified: userDoc?.phoneVerified,
    profileCompleted: userDoc?.profileCompleted,
    status: userDoc?.status,
    activeRole: role || userDoc?.role || sessionStorage.getItem('jobnvisa_registered_role'),
  });

  // Account suspended or banned
  if (userDoc.status === 'suspended' || userDoc.status === 'banned') {
    return <Navigate to="/auth/suspended" replace />;
  }

  // Account pending verification (skip redirect if offline to prevent false pending page on network drop)
  if (requireVerified && userDoc.status === 'pending_verification' && navigator.onLine) {
    return <Navigate to="/auth/pending" replace />;
  }

  // Verify email and phone before accessing dashboard (except superadmin, accountant, and helpdesk)
  const storedRole = sessionStorage.getItem('jobnvisa_registered_role') as UserRole | null;
  const activeRole = role || userDoc?.role || storedRole;
  const isEmailVerified = firebaseUser?.emailVerified || userDoc.emailVerified;
  const isPhoneVerified = userDoc.phoneVerified;
  if (activeRole !== 'superadmin' && activeRole !== 'accountant' && activeRole !== 'helpdesk' && (!isEmailVerified || !isPhoneVerified)) {
    return <Navigate to="/auth/verify" replace />;
  }
  if (allowedRoles && activeRole && !allowedRoles.includes(activeRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // First-Time Profile Completion Check for candidate, referrer, agency, employer
  const isProfileIncomplete = userDoc.profileCompleted !== true || !userDoc.country || !userDoc.country.trim();
  if (isProfileIncomplete && activeRole && activeRole !== 'superadmin' && activeRole !== 'accountant' && activeRole !== 'helpdesk') {
    if (activeRole === 'candidate' && location.pathname !== '/candidate/profile') {
      return <Navigate to="/candidate/profile" replace />;
    }
    if (activeRole === 'referrer' && location.pathname !== '/referrer/profile') {
      return <Navigate to="/referrer/profile" replace />;
    }
    if ((activeRole === 'agency' || activeRole === 'agency_staff') && location.pathname !== '/agency/settings') {
      return <Navigate to="/agency/settings" replace />;
    }
    if (activeRole === 'employer') {
      const isSettingsTab = location.pathname === '/employer' && location.search.includes('tab=settings');
      if (!isSettingsTab) {
        return <Navigate to="/employer?tab=settings" replace />;
      }
    }
  }

  return <>{children}</>;
}
